import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Chip,
    useTheme,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import DataTable from './DataTable';

// Category mapping dictionary
const categoryJSON = {
    "100": "alcoholic beverages",
    "111": "beers",
    "318": "beers",
    "112": "spirits",
    "326": "liqueurs",
    "330": "vodka",
    "331": "whisky",
    "114": "cocktails",
    "113": "wines",
    "101": "non alcoholic beverages",
    "186": "juice",
    "117": "water",
    "193": "tea",
    "576": "dairy beverages",
    "190": "smoothies",
    "118": "carbonated drinks",
    "184": "coffee",
    "103": "meat and seafood",
    "124": "meat alternatives",
    "287": "poultry",
    "581": "fish and seafood",
    "494": "fish",
    "582": "seafood",
    "578": "pork",
    "579": "beef",
    "104": "savory dishes",
    "125": "sandwiches",
    "361": "sandwiches",
    "129": "salads",
    "130": "sushi",
    "133": "noodles",
    "135": "egg dishes",
    "128": "soups",
    "397": "soups",
    "136": "burritos and tacos",
    "400": "tacos",
    "126": "meat based dishes",
    "127": "fish and seafood",
    "131": "rice",
    "379": "rice dishes",
    "469": "rice types",
    "132": "pasta",
    "376": "pasta",
    "467": "pasta",
    "134": "pizza",
    "137": "savory pies and pastries",
    "105": "desserts and sweet foods",
    "138": "cakes",
    "403": "cakes",
    "415": "sweet dishes",
    "583": "pastries",
    "414": "sweet pies",
    "139": "cookies",
    "405": "cookies",
    "413": "squares",
    "141": "frozen desserts",
    "410": "ice cream",
    "143": "breads",
    "144": "flours",
    "220": "cereals",
    "584": "waffles",
    "106": "grains",
    "146": "legumes",
    "147": "nuts and seeds",
    "145": "grains",
    "107": "sweet and savory snacks",
    "148": "savory snacks",
    "149": "sweet snacks",
    "108": "condiments",
    "150": "sauces",
    "527": "dressings",
    "541": "sauces",
    "151": "vinegar",
    "543": "vinegars",
    "537": "oils",
    "152": "spreads",
    "109": "food additives and seasonings",
    "155": "seasonings",
    "153": "supplements",
    "154": "sweeteners",
    "110": "fruits and vegetables",
    "156": "fruits",
    "430": "exotic fruits",
    "157": "vegetables",
    "181": "baking ingredients",
    "310": "baking confectionery",
    "580": "flour and doughs",
    "585": "plant based alternatives",
    "119": "dairy alternatives",
    "490": "milk alternatives",
    "282": "egg alternatives",
    "169": "dairy",
    "270": "cheese",
    "274": "milk",
    "276": "yogurt",
    "180": "confectionery",
    "309": "sugar confectionery",
    "308": "chocolate confectionery"
};

const ParametersTable = ({ parameters }) => {
    const theme = useTheme();

    if (!parameters) return null;

    // Process the parameters to handle category codes
    const processedParams = Object.entries(parameters).map(([key, value]) => {
        let displayKey = key;
        let displayValue = value;

        // Convert category codes to names for specific parameter keys
        if (key === 'category' || key === 'results_category') {
            if (Array.isArray(value)) {
                // Handle array of category codes
                displayValue = value.map(code => {
                    const categoryName = categoryJSON[code];
                    return categoryName ? `${code} (${categoryName})` : code;
                }).join(', ');
            } else if (value && categoryJSON[value]) {
                // Handle single category code
                displayValue = `${value} (${categoryJSON[value]})`;
            }
        } else {
            // For non-category parameters, handle arrays normally
            displayValue = Array.isArray(value) ? value.join(', ') : value;
        }

        return {
            parameter: displayKey,
            value: displayValue
        };
    });

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                mb: 0,
                borderRadius: '8px',
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                overflow: 'hidden'
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                fontWeight: 600,
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }}
                        >
                            Parameter
                        </TableCell>
                        <TableCell
                            sx={{
                                fontWeight: 600,
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }}
                        >
                            Value
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {processedParams.map((entry, index) => (
                        <TableRow
                            key={`param-${entry.parameter || index}`}
                            hover
                            sx={{
                                backgroundColor: index % 2 === 0
                                    ? 'transparent'
                                    : alpha(theme.palette.background.default, 0.6)
                            }}
                        >
                            <TableCell
                                component="th"
                                scope="row"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500
                                }}
                            >
                                {entry.parameter}
                            </TableCell>
                            <TableCell>
                                {typeof entry.value === 'string' && entry.value ? entry.value : String(entry.value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const MultiTableView = ({ toolResults, reformatted_question }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(0);
    const showParams = true;

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    // eslint-disable-next-line react/prop-types
    if (!toolResults || toolResults.length === 0) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="300px"
                sx={{
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                }}
            >
                <Box textAlign="center">
                    <TableChartOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                        No data available
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Sort results in descending order
    // const sortedResults = [...toolResults].reverse();
    const sortedResults = toolResults;
    console.log("sortedResults",sortedResults);


    return (
        <Box sx={{ mt: 1 }}>
            {reformatted_question !== null && reformatted_question !== undefined && reformatted_question !== "" && reformatted_question !== "None" && (
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    <b>Rephrased Question:</b> {reformatted_question}
                </Typography>
            )}
            
            {sortedResults.map((result, index) => {
                // Check if result has valid content
                const hasContent = result && result?.content && (
                    (Array.isArray(result.content) && result.content.length > 0) ||
                    (typeof result.content === 'object' && !Array.isArray(result.content) && Object.keys(result.content).length > 0)
                );
                
                if (!hasContent) {
                    console.log("Filtered out result (no content):", result);
                    return null;
                }
                
                console.log("Displaying result:", result);
                const endpointName = result?.endpoint || `endpoint ${sortedResults.length - index}`;
                const title = result?.title || endpointName;
                const rowCount = result?.endpoint === "trend" ? result?.content?.social?.length : (Array.isArray(result?.content) ? result?.content?.length : 0);
                const hasParameters = result?.parameters && Object.keys(result.parameters).length > 0;

                return (
                    <Accordion
                        key={`result-${title}-${index}`}
                        expanded={expanded === index}
                        onChange={handleAccordionChange(index)}
                        sx={{
                            mb: 2,
                            boxShadow: 'none',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            borderRadius: '8px !important',
                            '&:before': {
                                display: 'none',
                            },
                            overflow: 'hidden',
                            transition: 'all 0.2s ease',
                            backgroundColor: expanded === index
                                ? alpha(theme.palette.primary.main, 0.02)
                                : 'transparent',
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: expanded === index
                                    ? alpha(theme.palette.primary.main, 0.04)
                                    : 'rgba(0, 0, 0, 0.02)',
                                borderBottom: expanded === index ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={expanded === index ? 500 : 400}
                                        color={expanded === index ? 'primary.main' : 'text.primary'}
                                    >
                                        Tool: {title}
                                    </Typography>
                                </Box>

                                <Chip
                                    label={`${rowCount} rows`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 24,
                                        ml: 2,
                                        backgroundColor: 'transparent',
                                    }}
                                />
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                            {expanded === index && hasParameters && showParams && (
                                <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.background.paper, 0.6) }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 2,
                                            pb: 1,
                                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                                        }}
                                    >
                                        <SettingsIcon
                                            fontSize="small"
                                            sx={{
                                                mr: 1,
                                                color: "#E13C25",
                                                opacity: 0.8
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={500}
                                            color=" #E13C25"
                                        >
                                            Query Parameters
                                        </Typography>
                                    </Box>
                                    <ParametersTable parameters={result.parameters} />
                                </Box>
                            )}

                            {/* Visual separator between the two tables */}
                            {expanded === index && hasParameters && showParams && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        height: '24px',
                                        backgroundColor: alpha(theme.palette.background.default, 0.8),
                                        borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                                        borderBottom: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 2
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            px: 2,
                                            backgroundColor: alpha(theme.palette.background.default, 0.8)
                                        }}
                                    >
                                        Data Table
                                    </Typography>
                                </Box>
                            )}

                            <Paper
                                elevation={0}
                                sx={{
                                    maxHeight: '400px',
                                    overflow: 'auto',
                                    borderRadius: 0,
                                    '& table': {
                                        borderCollapse: 'separate',
                                        borderSpacing: 0,
                                    },
                                    '& th': {
                                        backgroundColor: theme.palette.mode === 'light'
                                            ? alpha(theme.palette.primary.main, 0.04)
                                            : alpha(theme.palette.primary.main, 0.15),
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        padding: '10px 16px',
                                    },
                                    '& td': {
                                        padding: '8px 16px',
                                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                    },
                                    '& tr:nth-of-type(even)': {
                                        backgroundColor: theme.palette.mode === 'light'
                                            ? 'rgba(0, 0, 0, 0.02)'
                                            : 'rgba(255, 255, 255, 0.02)'
                                    },
                                    '& tr:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                    }
                                }}
                            >
                                <DataTable
                                    data={result?.endpoint === "trend" ? result?.content?.social : result?.content}
                                />
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

ParametersTable.propTypes = {
    parameters: PropTypes.object
};

MultiTableView.propTypes = {
    toolResults: PropTypes.array,
    reformatted_question: PropTypes.string
};

export default MultiTableView;